import { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  loginAccount,
  loginWithGoogle,
  registerAccount,
  requestPasswordReset,
  resendHermesCode,
  resetPassword,
  verifyAccount,
} from "../../../shared/auth";
import { mountGoogleButton } from "../lib/google-web";
import { sendResendMail } from "../../../shared/api";
import { localKv } from "../lib/kv";

type Mode = "login" | "register" | "verify" | "forgot" | "reset";

type Props = {
  onAuthed: () => void;
};

export default function Login({ onAuthed }: Props) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const googleHostRef = useRef<HTMLDivElement | null>(null);

  const finish = () => {
    onAuthed();
    navigate("/", { replace: true });
  };

  const go = (next: Mode) => {
    setMode(next);
    setError("");
    setHint("");
    setCode("");
  };

  useEffect(() => {
    if (mode !== "login" || !googleHostRef.current) return;
    let cancelled = false;
    void mountGoogleButton(
      googleHostRef.current,
      (credential) => {
        if (cancelled) return;
        setBusy(true);
        setError("");
        loginWithGoogle(localKv, credential)
          .then(() => finish())
          .catch((err: unknown) => {
            setError(err instanceof Error ? err.message : "No se pudo entrar con Google");
          })
          .finally(() => setBusy(false));
      },
      (message) => {
        if (!cancelled) setError(message);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setHint("");
    try {
      if (mode === "login") {
        await loginAccount(localKv, { email, password });
        finish();
      } else if (mode === "register") {
        const result = await registerAccount(localKv, sendResendMail, { email, password, name });
        setMode("verify");
        setHint(
          result.mailSent
            ? `Te hemos enviado un código a ${email.trim().toLowerCase()}. Mira también en spam.`
            : "Revisa el correo o pulsa reenviar si no llega.",
        );
      } else if (mode === "verify") {
        await verifyAccount(localKv, { email, code });
        finish();
      } else if (mode === "forgot") {
        await requestPasswordReset(email);
        setMode("reset");
        setHint("Si existe una cuenta con ese correo, te hemos enviado un código.");
      } else {
        if (password.length < 8) throw new Error("La contraseña debe tener al menos 8 caracteres.");
        await resetPassword(localKv, { email, code: code.trim(), password });
        finish();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo continuar");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError("");
    try {
      await resendHermesCode(localKv, sendResendMail, email);
      setHint("Código reenviado. Revisa el correo.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo reenviar");
    }
  };

  const cta: Record<Mode, string> = {
    login: "Salir al mapa",
    register: "Crear cuenta Hermes",
    verify: "Validar código",
    forgot: "Enviar código",
    reset: "Cambiar contraseña y entrar",
  };

  return (
    <div className="login-street">
      <div className="login-skyline" aria-hidden />
      <a href="/hermes/" className="back-to-landing">
        ← Atrás
      </a>
      <section className="login-panel">
        <p className="login-kicker">RYCSO · CALLE</p>
        <h1 className="login-brand">HERMES</h1>
        <p className="login-lead">
          {mode === "verify"
            ? "Escribe el código del correo. Si ya tenías cuenta con este correo, el código te sirve para entrar y tu contraseña no cambia."
            : mode === "forgot" || mode === "reset"
              ? "Te enviaremos un código para elegir una contraseña nueva."
              : "Enciende el mapa. Guarda hobbies, material y planes con nombre de bar — no “ir a un café”."}
        </p>

        <form className="login-form" onSubmit={(e) => void submit(e)}>
          {mode === "register" ? (
            <label className="field">
              Nombre
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>
          ) : null}

          <label className="field">
            Correo
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>

          {mode === "verify" || mode === "reset" ? (
            <label className="field">
              Código Hermes
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>
          ) : null}

          {mode === "login" || mode === "register" || mode === "reset" ? (
            <label className="field">
              {mode === "reset" ? "Contraseña nueva (mínimo 8)" : "Contraseña"}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
              />
            </label>
          ) : null}

          {error ? <p className="form-error">{error}</p> : null}
          {hint ? <p className="form-hint">{hint}</p> : null}

          <button className="btn-copper" type="submit" disabled={busy}>
            {busy ? "…" : cta[mode]}
          </button>
        </form>

        {mode === "login" ? <div className="google-host" ref={googleHostRef} /> : null}

        {mode === "verify" ? (
          <button type="button" className="text-link" onClick={() => void resend()}>
            Reenviar código
          </button>
        ) : null}
        {mode === "login" || mode === "register" ? (
          <button type="button" className="text-link" onClick={() => go(mode === "login" ? "register" : "login")}>
            {mode === "login" ? "Aún no tengo cuenta" : "Ya tengo cuenta"}
          </button>
        ) : null}
        {mode === "login" || mode === "verify" ? (
          <button type="button" className="text-link" onClick={() => go("forgot")}>
            {mode === "login" ? "¿Olvidaste la contraseña?" : "¿Ya tenías cuenta y no recuerdas la contraseña?"}
          </button>
        ) : null}
        {mode !== "login" ? (
          <button type="button" className="text-link" onClick={() => go("login")}>
            Volver al inicio de sesión
          </button>
        ) : null}
      </section>
    </div>
  );
}
